import { ColumnInfo, ColumnInfoList, DisplaySetting } from 'components/shared/display_setting'
import { Flex } from 'components/shared/flex'
import { TableCell } from 'components/shared/widget'
import { GlobalStateContext } from 'contexts/global_state_context'
import { PCR_RUN_STATUS_SELECT_ITEMS, PcrRun } from 'entities/pcr_run'
import { ProductItem } from 'entities/product_item'
import _ from 'lodash'
import { ReactNode, useContext, useMemo } from 'react'
import { Badge, OverlayTrigger, Popover } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { Form } from 'utils/hooks'
import { PcrRunViewModel } from 'view_models/pcr_run_view_model'
import { SampleViewModel } from 'view_models/sample_view_model'
import { ProcessLogsModal } from './process_log_modal'

export const pcrRunColumnInfoList = (form?: Form<Record<string, unknown>>): ColumnInfoList => {
    const globalState = useContext(GlobalStateContext)
    const values: ColumnInfo[] = [
        { name: 'ID', th: true, width: 120, align: 'center', form: form, orderAttr: 'pcr_run_id' },
        {
            name: 'RUN ID',
            th: true,
            width: 120,
            align: 'center',
            form: form,
            orderAttr: 'pcr_run_id',
            searchType: { name: 'pcrRunRunId', type: 'multiple_text' },
        },
        {
            name: '作成者',
            th: true,
            width: 140,
            form: form,
            orderAttr: 'pcr_run_user_id',
            searchType: {
                name: 'pcrRunUserId',
                type: 'multiple_select',
                selectItems: globalState.users.map((user) => ({ label: user.fullName!, value: user.id! })),
            },
        },
        { name: '作成日', th: true, width: 100, form: form, orderAttr: 'pcr_run_created_at', searchType: { name: 'pcrRunCreatedAt', type: 'date' } },
        { name: 'Rawデータ', th: true, width: 120 },
        { name: '製品', th: true, width: 250 },
        {
            name: 'ステータス',
            align: 'center',
            th: true,
            width: 90,
            form: form,
            orderAttr: 'pcr_run_status',
            searchType: { name: 'status', type: 'multiple_select', selectItems: PCR_RUN_STATUS_SELECT_ITEMS },
        },
        { name: '結果ログ', align: 'center', th: true, width: 90 },
        // { name: 'Wellエラー', align: 'center', th: true, width: 100 },
        { name: 'cDNA', align: 'center', th: true, width: 100 },
        { name: 'Probe', align: 'center', th: true, width: 100 },
        { name: 'qPCR機器', th: true, width: 140, form: form, orderAttr: 'pcr_run_equipment_type' },
        { name: '自動分注機', th: true, width: 140, form: form, orderAttr: 'pcr_run_dispenser' },
        { name: '自動分注機チップ', th: true, width: 250, form: form, orderAttr: 'pcr_run_dispenser_chip' },
        { name: 'Master mix', align: 'center', th: true, width: 120 },
        { name: 'Free water', align: 'center', th: true, width: 120 },
        { name: '作業時エラー', th: true, width: 200 },
        { name: '備考', th: true, minWidth: 300 },
    ]
    return {
        tableName: 'PCR RUN',
        values: values,
    }
}

export type PcrRunTableDataProps = {
    pcrRun?: PcrRun
    displaySetting: DisplaySetting
    onClickDetail: () => void
    productItems?: ProductItem[]
}

export const PcrRunTableData = (props: PcrRunTableDataProps): JSX.Element => {
    if (!props.pcrRun) {
        return (
            <>
                {props.displaySetting.columnInfoList.values.map((_: ColumnInfo, index: number) => {
                    if (props.displaySetting.hiddenList.includes(index)) {
                        return <></>
                    } else {
                        return <TableCell key={index}></TableCell>
                    }
                })}
            </>
        )
    }

    const getProductItems = (): ProductItem[] => {
        if (!props.productItems || props.productItems.length === 0) {
            return []
        }
        const runProbes = props.pcrRun!.probes!.map((p) => p.id!)
        const productItems: ProductItem[] = []
        props.productItems.forEach((pi) => {
            const productProbes = pi.currentProtocolVersion!.protocolVersionProbes!.map((pvp) => pvp.probe!.id!)!

            if (productProbes.length > 0 && _.difference(productProbes, runProbes).length == 0 && !productItems.find((i) => i.id === pi.id)) {
                productItems.push(pi)
                return
            }
        })
        if (productItems.length === 0) {
            return [{ name: '標準尿精度管理', color: '#f00' }]
        }
        return productItems
    }

    const createCells = useMemo(() => {
        const vm = new PcrRunViewModel(props.pcrRun!)
        const sampleVm = new SampleViewModel({ productItems: getProductItems() })
        return (
            <>
                <TableCell align={'center'}>{props.pcrRun?.id}</TableCell>
                <TableCell align={'center'}>
                    <div
                        className="linked-text"
                        onClick={() => {
                            props.onClickDetail()
                        }}
                    >
                        {props.pcrRun?.runId}
                    </div>
                </TableCell>
                <TableCell>{props.pcrRun?.user?.fullName}</TableCell>
                <TableCell>{vm.createDate()}</TableCell>
                <TableCell>{props.pcrRun?.rawData && <a href={props.pcrRun.rawData! as string}>ダウンロード</a>}</TableCell>
                <TableCell align="center">
                    <Flex alignItems="center">{sampleVm.productItemView()}</Flex>
                </TableCell>
                <TableCell align="center">{vm.statusView()}</TableCell>
                <TableCell align="center">
                    {props.pcrRun?.status !== 'NOT_STARTED' && <ProcessLogsModal smallButton pcrRunId={props.pcrRun?.runId} />}
                </TableCell>
                {/* <TableCell align="center" unit={'件'}>
                    {props.pcrRun?.pcrErrorCount}
                </TableCell> */}
                <TableCell align="center">
                    <OverlayTrigger
                        trigger="hover"
                        placement="right"
                        overlay={
                            <Popover id="popover-basic">
                                <Popover.Header as="h3">cDNA一覧</Popover.Header>
                                <Popover.Body>
                                    <>
                                        {props.pcrRun!.dnaSet?.map((dna) => {
                                            return <div key={'dnaid-' + dna.id}>{dna.code}</div>
                                        })}
                                        <div>qPCR_standard</div>
                                    </>
                                </Popover.Body>
                            </Popover>
                        }
                    >
                        {/* blankとqPCR_standard分 + 2 */}
                        <Badge>{props.pcrRun!.dnaSet!.length + 1} cDNA</Badge>
                    </OverlayTrigger>
                </TableCell>
                <TableCell align="center">
                    <OverlayTrigger
                        trigger="hover"
                        placement="right"
                        overlay={
                            <Popover id="popover-basic">
                                <Popover.Header as="h3">Probe一覧</Popover.Header>
                                <Popover.Body>
                                    <>
                                        {props.pcrRun!.probes?.map((probe) => {
                                            return <div key={'probeid-' + probe.id}>{probe.name}</div>
                                        })}
                                    </>
                                </Popover.Body>
                            </Popover>
                        }
                    >
                        <Badge>{props.pcrRun!.probes!.length} probe</Badge>
                    </OverlayTrigger>
                </TableCell>

                <TableCell>{props.pcrRun?.equipmentType}</TableCell>
                <TableCell>{props.pcrRun?.dispenser}</TableCell>
                <TableCell>{props.pcrRun?.dispenserChip}</TableCell>
                <TableCell align="center">
                    {props.pcrRun?.masterMixReagentItemId ? (
                        <Link to={`reagent_items?reagent_kind=master_mix&id=${props.pcrRun?.masterMixReagentItemId}`}>
                            ID: {props.pcrRun?.masterMixReagentItemId}
                        </Link>
                    ) : (
                        props.pcrRun?.masterMix
                    )}
                </TableCell>
                <TableCell align="center">
                    {props.pcrRun?.pcrFreeWaterReagentItemId && (
                        <Link to={`reagent_items?reagent_kind=free_water&id=${props.pcrRun?.pcrFreeWaterReagentItemId}`}>
                            ID: {props.pcrRun?.pcrFreeWaterReagentItemId}
                        </Link>
                    )}
                </TableCell>
                <TableCell>{props.pcrRun?.errorNote}</TableCell>
                <TableCell>{props.pcrRun?.note}</TableCell>
            </>
        )
    }, [props.displaySetting.hiddenList, props.productItems])

    return createCells.props.children.filter((_: ReactNode, index: number) => !props.displaySetting.hiddenList?.includes(index)).map((cell: ReactNode) => cell)
}
